import { jsxs, jsx } from "react/jsx-runtime";
import { BoxSelection, BoxSelectionBoxSize, TextButton, Text, Checkbox } from "@wix/wix-ui-tpa/cssVars";
import React__default, { useRef, useEffect } from "react";
import { useConfig } from "../../../../contexts/config-context.js";
import { useFocusFieldEvent } from "../../../../contexts/events-context.js";
import { useForcedState, ForcedState } from "../../../../contexts/forced-state-context.js";
import { useTranslation } from "../../../../contexts/translation-context.js";
import { DataStatus, bulkSlotsSize } from "../../appointment-state.js";
import { Components, Actions } from "../../bi-types.js";
import { useAppointmentContext } from "../../context/appointment-context.js";
import { Loader } from "../../loader/index.js";
import { Notification } from "../../notification/index.js";
import { useAppointmentBi } from "../../utils/bi.js";
import { st, classes } from "./slots.st.css";
import { getSlotKey, getSlotIndex, getFormattedDateLabel, formatTimeRange, getSlotByKey } from "./utils.js";
const TimeSlots = ({ isReloadingSlots, fieldId }) => {
  const { appointmentFieldProps, appointmentState, setAppointmentState } = useAppointmentContext();
  const selectedSlot = appointmentFieldProps?.value;
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = React__default.useState(
    () => getSlotKey(selectedSlot)
  );
  const [shouldFocus, setShouldFocus] = React__default.useState(false);
  const { isMobile, isComposer, language } = useConfig();
  const {
    availabilitySlots,
    timezone = "",
    currentSlotBulkSize,
    availabilityDates
  } = appointmentState || {};
  const bi = useAppointmentBi();
  const forcedState = useForcedState();
  const boxSelectionRef = useRef(void 0);
  const visibility = isReloadingSlots || appointmentState?.dataStatus === DataStatus.LOADING ? "hidden" : "visible";
  const firstSlotRef = useRef(null);
  useFocusFieldEvent(() => {
    firstSlotRef.current?.focus();
  }, fieldId);
  useEffect(() => {
    if (shouldFocus && currentSlotBulkSize) {
      boxSelectionRef.current?.focus(currentSlotBulkSize - 4);
    }
  }, [shouldFocus, boxSelectionRef, currentSlotBulkSize]);
  useEffect(() => {
    setSelectedId(getSlotKey(selectedSlot));
    if (availabilitySlots?.length && currentSlotBulkSize) {
      const key = getSlotKey(selectedSlot);
      const slotIndex = getSlotIndex(key, availabilitySlots);
      if (slotIndex && slotIndex + 1 > currentSlotBulkSize) {
        setAppointmentState({
          currentSlotBulkSize: slotIndex + 1
        });
      }
    } else if (!availabilitySlots?.length) {
      appointmentFieldProps?.onChange?.(void 0);
    }
    setShouldFocus(false);
  }, [selectedSlot, availabilitySlots, availabilityDates]);
  const onSlotSelected = (slotId) => {
    if (appointmentFieldProps?.onChange && availabilitySlots) {
      const newSelectedSlot = getSlotByKey(slotId, availabilitySlots);
      if (newSelectedSlot?.slot?.endDate && newSelectedSlot?.slot?.startDate) {
        bi?.report({
          action: Actions.CLICK,
          component: Components.SLOT,
          startTime: new Date(newSelectedSlot.slot.startDate).valueOf(),
          endTime: new Date(newSelectedSlot.slot.endDate).valueOf()
        });
        appointmentFieldProps?.onChange({
          startDate: newSelectedSlot.slot.startDate,
          endDate: newSelectedSlot.slot.endDate,
          timeZone: timezone
        });
      }
    }
  };
  const isMoreSlotsToDisplay = () => {
    const bulkSize = appointmentState.currentSlotBulkSize || bulkSlotsSize;
    return !!(appointmentState?.availabilitySlots?.length && bulkSize < appointmentState?.availabilitySlots?.length);
  };
  const onShowMoreClicked = () => {
    if (isMoreSlotsToDisplay()) {
      bi?.report({
        action: Actions.CLICK,
        component: Components.SHOW_MORE
      });
      const bulkSize = appointmentState.currentSlotBulkSize || bulkSlotsSize;
      setShouldFocus(true);
      setAppointmentState({
        currentSlotBulkSize: bulkSize + bulkSlotsSize
      });
    }
  };
  const renderSlots = () => {
    if (!availabilitySlots?.length) {
      return /* @__PURE__ */ jsx(Notification, { message: t("form.appointment.slots-not-found.text") });
    }
    return availabilitySlots.slice(0, appointmentState.currentSlotBulkSize).map((slot, index) => {
      if (!slot.slot || !slot.slot.startDate || !slot.slot.endDate) {
        return null;
      }
      const { startDate, endDate } = slot.slot;
      const timeRange = formatTimeRange({
        startDate,
        endDate,
        locale: language,
        timezone
      });
      const key = getSlotKey({
        startDate: slot?.slot?.startDate,
        endDate: slot?.slot?.endDate
      });
      return /* @__PURE__ */ jsx(
        BoxSelection.Option,
        {
          id: key,
          className: classes.boxSelectionOption,
          checked: selectedId === key,
          "aria-label": timeRange,
          ref: index === 0 ? firstSlotRef : void 0,
          children: /* @__PURE__ */ jsx(Text, { className: classes.timeRange, children: timeRange })
        },
        key
      );
    });
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: st(classes.root, {
        isMobile,
        forcedHover: forcedState.includes(ForcedState.INPUT_FIELDS_HOVER),
        isComposer
      }),
      children: [
        isReloadingSlots && /* @__PURE__ */ jsx("div", { className: classes.verticalCenterLoader, children: /* @__PURE__ */ jsx(Loader, {}) }),
        /* @__PURE__ */ jsxs("div", { style: { visibility }, children: [
          /* @__PURE__ */ jsx(
            BoxSelection,
            {
              size: BoxSelectionBoxSize.small,
              className: classes.boxSelection,
              name: "hour-selection",
              label: getFormattedDateLabel({
                selectedDate: appointmentState.selectedDate,
                locale: language,
                timezone
              }),
              onChange: ({ id }) => onSlotSelected(id),
              ref: boxSelectionRef,
              children: renderSlots()
            }
          ),
          !!isMoreSlotsToDisplay() && /* @__PURE__ */ jsx(
            TextButton,
            {
              className: classes.textButton,
              onClick: () => onShowMoreClicked(),
              children: t("form.appointment.show-more-slots.text")
            }
          )
        ] })
      ]
    }
  );
};
TimeSlots.includeCheckboxInBundle = Checkbox;
export {
  TimeSlots
};
//# sourceMappingURL=index.js.map
